import React, { useEffect, useState } from 'react';
import {
  course_courses_empty_myCompleted,
  course_courses_empty_myInProgress,
  course_courses_empty_userCompleted,
  course_courses_empty_userInProgress,
  course_courses_empty_myPublished,
  course_courses_empty_userPublished,
  course_courses_tabs_inProgress,
  course_courses_tabs_completed,
  course_courses_tabs_published,
  course_courses_tabs_drafts
} from '@paraglide/messages';
import styled from '@emotion/styled';
import { TabNav } from "@edusense/shared-ui-layout";
import { CourseList } from "@edusense/course-ui";
import { useCourseData } from '@edusense/features-course-hooks';
import { GetApiCoursesStatus, GetApiCoursesType } from '@edusense/course-client';
import ProfileCoursesDraftTab from './ProfileCoursesDraftTab';
import type { Course } from '@edusense/course-types';
import { PROFILE_PAGE_TEST_IDS } from '@edusense/shared-constants';
import { useNavigate } from '../../../../../src/hooks/useI18nNavigate';
import toast from 'react-hot-toast';

// Lucide React 직접 import - Vite 번들링 최적화 및 초기화 순서 문제 해결
import BookOpen from 'lucide-react/dist/esm/icons/book-open';

export interface TabItem {
  id: string;
  label: string;
}

// CourseStatusType 정의 (useCourseData 훅의 status와 일치시키거나 import 필요)
export type CourseStatusType = typeof GetApiCoursesStatus[keyof typeof GetApiCoursesStatus];

interface ProfileCoursesTabProps {
  userId: string;
  ownerUsername?: string;
  isMyProfile?: boolean;
  mode: 'enrolled' | 'created'; // 필수 prop으로 변경
}

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;
  color: var(--theme-text-secondary);
`;

const BookIcon = styled(BookOpen)`
  width: 3rem;
  height: 3rem;
  opacity: 0.5;
`;

const TabsContainer = styled.div`
  margin-bottom: 1rem;
  position: relative;
  z-index: 5;
`;

const ProfileCoursesTab: React.FC<ProfileCoursesTabProps> = ({
  userId,
  ownerUsername,
  isMyProfile = false,
  mode
}) => {
  const navigate = useNavigate();

  // "enrolled" 모드 하위 탭 상태 (수강 중 / 완료)
  const [enrolledCourseSubTab, setEnrolledCourseSubTab] = useState<CourseStatusType>('in-progress');
  
  // "created" 모드 하위 탭 상태 (게시된 강의 / 초안)
  const [createdCourseSubTab, setCreatedCourseSubTab] = useState<'published' | 'drafts'>('published');

  useEffect(() => {
    // mode prop이 바뀔 때마다 각 모드에 맞는 하위 탭을 기본값으로 리셋
    console.log(`ProfileCoursesTab useEffect: mode prop is "${mode}".`);
    if (mode === 'enrolled') {
      console.log(` Mode is 'enrolled'. Resetting createdCourseSubTab to 'published' and enrolledCourseSubTab to 'in-progress'.`);
      setCreatedCourseSubTab('published'); // 다른 모드의 하위 탭 리셋
      setEnrolledCourseSubTab('in-progress'); // 현재 모드의 하위 탭 기본값 설정
    } else if (mode === 'created') {
      console.log(` Mode is 'created'. Resetting enrolledCourseSubTab to 'in-progress' and createdCourseSubTab to 'published'.`);
      setEnrolledCourseSubTab('in-progress'); // 다른 모드의 하위 탭 리셋
      setCreatedCourseSubTab('published'); // 현재 모드의 하위 탭 기본값 설정
    }
  }, [mode]);

  // 수강 중/완료한 강의 데이터 (enrolled 모드)
  const enrolledCoursesData = useCourseData({
    type: GetApiCoursesType.ENROLLED,
    userId,
    status: enrolledCourseSubTab, // 동적으로 하위 탭 상태 사용
    enabled: !!userId && mode === 'enrolled' // mode가 'enrolled'일 때만 활성화
  });
  
  // 내가 "게시한" 강의 데이터 (created 모드, published 하위 탭)
  const publishedCourseType = isMyProfile ? GetApiCoursesType.MY_PUBLISHED : GetApiCoursesType.USER;

  const instructorIdentifier = !isMyProfile && ownerUsername
    ? ownerUsername
    : (isMyProfile ? undefined : userId);

  const publishedCreatedCoursesData = useCourseData({
    type: publishedCourseType, // 내가 본인일 때 MY_PUBLISHED, 아니면 USER로 조회
    userId: instructorIdentifier,
    enabled: !!userId && mode === 'created' && createdCourseSubTab === 'published'
  });

  let currentHookResult: any = {}; 
  let coursesToDisplay: Course[] = [];

  if (mode === 'enrolled') {
    currentHookResult = enrolledCoursesData;
    coursesToDisplay = enrolledCoursesData.data || [];
  } else if (mode === 'created' && createdCourseSubTab === 'published') {
    currentHookResult = publishedCreatedCoursesData;
    coursesToDisplay = publishedCreatedCoursesData.data || [];
  }
  // created 모드이고 createdCourseSubTab === 'drafts' 인 경우는 ProfileCoursesDraftTab 에서 자체적으로 데이터 관리

  const {
    isLoading,
    isFetchingNextPage,
    error,
    hasNextPage,
    fetchNextPage,
    observerRef
    // data는 위에서 coursesToDisplay로 이미 처리
  } = currentHookResult;
  
  // 빈 상태 메시지
  const getEmptyStateMessage = () => {
    if (mode === 'enrolled') {
      return isMyProfile
        ? enrolledCourseSubTab === 'completed' 
          ? course_courses_empty_myCompleted()
          : course_courses_empty_myInProgress()
        : enrolledCourseSubTab === 'completed'
          ? course_courses_empty_userCompleted()
          : course_courses_empty_userInProgress();
    } else { // mode === 'created'
      if (createdCourseSubTab === 'published') {
        return isMyProfile
          ? course_courses_empty_myPublished()
          : course_courses_empty_userPublished();
      }
      // 'drafts'의 빈 상태 메시지는 ProfileCoursesDraftTab 내부에서 처리
      return ''; // 또는 기본 메시지
    }
  };
  
  // 빈 상태 콘텐츠
  const emptyStateContent = (
    <EmptyStateContainer>
      <BookIcon />
      <div>{getEmptyStateMessage()}</div>
    </EmptyStateContainer>
  );
  
  // 다른 사용자 프로필에서 "내가 등록한 강의" 탭 (mode === 'created')을 어떻게 보여줄지 결정합니다.
  // 이 로직은 isMyProfile && mode === 'created' 일때 하위 탭 Nav를 보여주는 부분과 중복될 수 있어 검토 필요.
  // 만약 타인 프로필의 'created' 모드는 항상 'published'만 보여준다면 이 블록은 유효.
  // 하지만, 새로운 구조에서는 ProfileTab.tsx에서 'teaching'(mode=created) 탭은 isMyProfile일때만 보이도록 제한했으므로,
  // 이 조건 블록 (!isMyProfile && mode === 'created')은 실행될 일이 없을 수 있음. 일단 유지하고 테스트 시 확인.
  if (!isMyProfile && mode === 'created') {
    return (
      <CourseList
        courses={publishedCreatedCoursesData.data || []}
        isLoading={publishedCreatedCoursesData.isLoading}
        isFetchingNextPage={publishedCreatedCoursesData.isFetchingNextPage}
        error={publishedCreatedCoursesData.error}
        hasNextPage={publishedCreatedCoursesData.hasNextPage}
        fetchNextPage={publishedCreatedCoursesData.fetchNextPage}
        observerRef={publishedCreatedCoursesData.observerRef}
        emptyStateContent={(
          <EmptyStateContainer>
            <BookIcon />
            <div>{course_courses_empty_userPublished()}</div>
          </EmptyStateContainer>
        )}
        itemDataTestId={PROFILE_PAGE_TEST_IDS.PROFILE_COURSES_LIST_ITEM}
        isDraft={false}
      />
    );
  }
  
  // "enrolled" 모드 하위 탭 UI를 위한 TabItem 정의
  const enrolledSubTabs: TabItem[] = [
    { id: 'in-progress', label: course_courses_tabs_inProgress(), dataTestId: PROFILE_PAGE_TEST_IDS.PROFILE_COURSES_SUBTAB_IN_PROGRESS },
    { id: 'completed', label: course_courses_tabs_completed(), dataTestId: PROFILE_PAGE_TEST_IDS.PROFILE_COURSES_SUBTAB_COMPLETED }
  ];

  // "created" 모드 하위 탭 UI를 위한 TabItem 정의 (기존 유지)
  const createdSubTabs: TabItem[] = [
    { id: 'published', label: course_courses_tabs_published(), dataTestId: PROFILE_PAGE_TEST_IDS.PROFILE_COURSES_SUBTAB_PUBLISHED },
    { id: 'drafts', label: course_courses_tabs_drafts(), dataTestId: PROFILE_PAGE_TEST_IDS.PROFILE_COURSES_SUBTAB_DRAFTS }
  ];

  // 강의 편집 핸들러
  const handleEdit = (courseId: string) => {
    const editPath = `/learn/course/${courseId}/edit`;
    navigate(editPath);
  };

  // 발행 취소 핸들러 (placeholder - API가 구현되면 실제 호출로 교체)
  const handleUnpublish = (courseId: string) => {
    // TODO: unpublish API 구현 후 실제 API 호출로 교체
    toast.error('발행 취소 기능은 아직 구현되지 않았습니다.');
    console.log('Unpublish requested for course:', courseId);
  };

  // ========================== 통합 디버깅 로그 추가 ==========================
  console.log('ProfileCoursesTab RENDER (New Structure):', {
    userId,
    isMyProfile,
    mode,
    enrolledCourseSubTab, // "수강 중/완료" 하위 탭 상태
    createdCourseSubTab, // "게시/초안" 하위 탭 상태
  });
  // ========================================================================
  
  return (
    <>
      {/* 메인 TabNav는 제거됨. 이제 이 컴포넌트는 특정 mode에 대한 컨텐츠만 담당 */}
      {/* <TabsContainer>
        <TabNav
          activeTabId={activeTab} // activeTab 상태 제거됨
          tabs={mainTabs}         // mainTabs 제거됨
          onTabChange={(tabId) => setActiveTab(tabId as 'enrolled' | 'created')}
          variant="indicator"
        />
      </TabsContainer> */}

      {/* "enrolled" (학습) 모드일 때 하위 탭 (수강 중 / 수강 완료) 표시 */}
      {mode === 'enrolled' && (
        <TabsContainer style={{ marginLeft: '1rem', marginRight: '1rem', marginTop: '0.5rem', marginBottom: '1.5rem' }}>
          <TabNav
            tabs={enrolledSubTabs}
            activeTabId={enrolledCourseSubTab}
            onTabChange={(tabId) => setEnrolledCourseSubTab(tabId as CourseStatusType)}
            variant="indicator"
          />
        </TabsContainer>
      )}

      {/* "created" (강의) 모드이고 내 프로필일 때만 하위 탭 (게시된 강의 / 초안) 표시 */}
      {mode === 'created' && isMyProfile && (
        <TabsContainer style={{ marginLeft: '1rem', marginRight: '1rem', marginTop: '0.5rem', marginBottom: '1.5rem' }}>
          <TabNav
            tabs={createdSubTabs}
            activeTabId={createdCourseSubTab}
            onTabChange={(tabId) => setCreatedCourseSubTab(tabId as 'published' | 'drafts')}
            variant="indicator"
          />
        </TabsContainer>
      )}
      
      {/* 현재 선택된 모드와 하위 탭에 따라 적절한 컨텐츠 표시 */}
      {mode === 'enrolled' && (
        <CourseList
          courses={coursesToDisplay}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          error={error}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          observerRef={observerRef}
          emptyStateContent={emptyStateContent}
          itemDataTestId={PROFILE_PAGE_TEST_IDS.PROFILE_COURSES_LIST_ITEM}
        />
      )}
      {mode === 'created' && createdCourseSubTab === 'published' && (
        <CourseList
          courses={coursesToDisplay} // publishedCreatedCoursesData에서 온 데이터
          isLoading={publishedCreatedCoursesData.isLoading} // created 모드 데이터 로딩 상태
          isFetchingNextPage={publishedCreatedCoursesData.isFetchingNextPage}
          error={publishedCreatedCoursesData.error}
          hasNextPage={publishedCreatedCoursesData.hasNextPage}
          fetchNextPage={publishedCreatedCoursesData.fetchNextPage}
          observerRef={publishedCreatedCoursesData.observerRef}
          emptyStateContent={emptyStateContent}
          itemDataTestId={PROFILE_PAGE_TEST_IDS.PROFILE_COURSES_LIST_ITEM}
          isDraft={false}
          onEdit={handleEdit}
          onUnpublish={handleUnpublish}
        />
      )}
      {mode === 'created' && createdCourseSubTab === 'drafts' && isMyProfile && (
        <ProfileCoursesDraftTab userId={userId} isMyProfile={isMyProfile} />
      )}
    </>
  );
};

export default ProfileCoursesTab; 
